import { useState, useEffect, useCallback } from 'react';
import personaService from '../services/personaService'; // Importer l'instance apiClient configurée
import i18n from '../i18n/config';
import { Persona } from '../types'; // Ajustez le chemin si nécessaire

// Type pour la mise à jour des liens persona
type UseCasePersonaLink = { persona_ids: string[] };

interface UseUseCasePersonasResult {
  associatedPersonas: Persona[];
  isLoading: boolean;
  error: string | null;
  fetchAssociatedPersonas: () => Promise<void>;
  updateAssociatedPersonas: (personaIds: string[]) => Promise<boolean>; // Retourne true si succès
}

export const useUseCasePersonas = (useCaseId: string | undefined): UseUseCasePersonasResult => {
  const [associatedPersonas, setAssociatedPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApiError = (err: any, context: string, fallbackKey: string): string => {
    console.error(`Error ${context}:`, err);
    const errorMsg = err.response?.data?.detail || err.message || i18n.t(fallbackKey);
    setError(errorMsg);
    return errorMsg;
  };

  const fetchAssociatedPersonas = useCallback(async () => {
    if (!useCaseId) return;
    setIsLoading(true);
    setError(null);
    try {
      // Assurez-vous que le type Persona correspond à la réponse API
      const data = await personaService.listByUseCase(useCaseId);
      setAssociatedPersonas(data || []);
    } catch (err) {
      handleApiError(err, 'fetching associated personas', 'errors.persona.fetchAssociatedFailed');
      setAssociatedPersonas([]);
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId]);

  const updateAssociatedPersonas = useCallback(async (personaIds: string[]): Promise<boolean> => {
    if (!useCaseId) {
      setError(i18n.t('errors.useCase.missingId'));
      return false;
    }
    setIsLoading(true);
    setError(null);
    const payload: UseCasePersonaLink = { persona_ids: personaIds };
    try {
      await personaService.updateForUseCase(useCaseId, payload);
      // Recharger les personas après la mise à jour pour confirmer
      await fetchAssociatedPersonas();
      return true; // Succès
    } catch (err) {
      handleApiError(err, 'updating associated personas', 'errors.persona.updateAssociatedFailed');
      return false; // Échec
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchAssociatedPersonas]);

  // Charger les personas initiaux
  useEffect(() => {
    if (useCaseId) {
      fetchAssociatedPersonas();
    } else {
      // Réinitialiser si pas d'ID
      setAssociatedPersonas([]);
      setError(null);
    }
  }, [useCaseId, fetchAssociatedPersonas]); // fetchAssociatedPersonas est stable grâce à useCallback

  return {
    associatedPersonas,
    isLoading,
    error,
    fetchAssociatedPersonas,
    updateAssociatedPersonas,
  };
};
